import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import api from "../lib/api";
import { toast } from "sonner";
import { NbButton, NbTextarea, NbSelect } from "../components/nb";
import { Heart, Star, MessageCircle, BadgeCheck, MapPin, Briefcase, GraduationCap, Crown, Ban, Flag, ArrowLeft } from "lucide-react";

export default function MemberDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [m, setM] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [report, setReport] = useState({ reason: "Inappropriate content", details: "" });

  const load = async () => {
    try { const { data } = await api.get(`/members/${id}`); setM(data); }
    catch { toast.error("Member not found."); navigate("/browse"); }
  };
  useEffect(() => { load(); }, [id]);

  const like = async () => {
    const { data } = await api.post(`/like/${id}`);
    setM({ ...m, liked: data.liked });
    if (data.matched) toast.success(`It's a match! 🎉`);
  };
  const fav = async () => { const { data } = await api.post(`/favorite/${id}`); setM({ ...m, favorited: data.favorited }); };
  const block = async () => { const { data } = await api.post(`/block/${id}`); toast.success(data.blocked ? "User blocked." : "User unblocked."); if (data.blocked) navigate("/browse"); };
  const submitReport = async () => {
    try { await api.post("/report", { reported_user_id: id, ...report }); toast.success("Report submitted. Our team will review it."); setReportOpen(false); }
    catch { toast.error("Failed to submit report."); }
  };

  if (!m) return <div className="min-h-screen grain"><Navbar /><div className="text-center py-20 font-head font-bold uppercase">Loading...</div></div>;

  const photos = m.photos?.length ? m.photos : (m.avatar ? [m.avatar] : [`https://api.dicebear.com/7.x/thumbs/svg?seed=${m.id}`]);

  return (
    <div className="min-h-screen grain">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 font-head font-bold uppercase text-sm mb-4 nb-hover"><ArrowLeft className="w-4 h-4" /> Back</button>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <div className="aspect-square nb-border nb-shadow overflow-hidden bg-white">
              <img src={photos[0]} alt={m.name} className="w-full h-full object-cover" />
            </div>
            {photos.length > 1 && (
              <div className="grid grid-cols-4 gap-2 mt-2">
                {photos.slice(1, 5).map((p, i) => <div key={i} className="aspect-square nb-border overflow-hidden"><img src={p} alt="" className="w-full h-full object-cover" /></div>)}
              </div>
            )}
          </div>

          <div>
            <div className="bg-white nb-border nb-shadow p-6">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-head font-black text-3xl tracking-tight">{m.name}{m.age ? `, ${m.age}` : ""}</h1>
                {m.verified_badge && <BadgeCheck className="w-6 h-6 text-[#D4AF37]" fill="#0A0A0A" />}
                {m.membership === "premium" && <span className="bg-[#D4AF37] nb-border px-2 py-0.5 text-xs font-head font-bold uppercase flex items-center gap-1"><Crown className="w-3 h-3" /> Premium</span>}
              </div>
              <div className="flex flex-wrap gap-3 mt-3 text-sm font-medium text-gray-700">
                {m.location && <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{m.location}</span>}
                {m.occupation && <span className="flex items-center gap-1"><Briefcase className="w-4 h-4" />{m.occupation}</span>}
                {m.education && <span className="flex items-center gap-1"><GraduationCap className="w-4 h-4" />{m.education}</span>}
              </div>
              {(m.gender || m.orientation || m.looking_for) && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {m.gender && <span className="bg-[#F3E8E6] nb-border px-2 py-1 text-xs font-head font-bold uppercase">{m.gender}</span>}
                  {m.orientation && <span className="bg-[#D4AF37] nb-border px-2 py-1 text-xs font-head font-bold uppercase">{m.orientation}</span>}
                  {m.looking_for && <span className="bg-[#F3F3F1] nb-border px-2 py-1 text-xs font-head font-bold uppercase">Wants: {m.looking_for}</span>}
                </div>
              )}
              <p className="mt-4 font-medium text-gray-800">{m.bio || "No bio yet."}</p>
              {m.interests?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-4">
                  {m.interests.map((i) => <span key={i} className="bg-white nb-border px-2 py-1 text-xs font-head font-bold uppercase">{i}</span>)}
                </div>
              )}
            </div>

            <div className="grid grid-cols-3 gap-2 mt-4">
              <NbButton onClick={like} data-testid="detail-like" className={m.liked ? "" : ""} variant={m.liked ? "primary" : "outline"}><Heart className="w-4 h-4" fill={m.liked ? "white" : "none"} /> Like</NbButton>
              <NbButton onClick={fav} variant={m.favorited ? "accent" : "outline"} data-testid="detail-fav"><Star className="w-4 h-4" fill={m.favorited ? "#0A0A0A" : "none"} /> Fav</NbButton>
              <NbButton as={Link} to={`/messages/${m.id}`} variant="secondary" data-testid="detail-message"><MessageCircle className="w-4 h-4" /> Message</NbButton>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <NbButton onClick={block} variant="outline" data-testid="detail-block" className="text-xs"><Ban className="w-4 h-4" /> Block</NbButton>
              <NbButton onClick={() => setReportOpen(!reportOpen)} variant="danger" data-testid="detail-report" className="text-xs"><Flag className="w-4 h-4" /> Report</NbButton>
            </div>

            {reportOpen && (
              <div className="bg-white nb-border nb-shadow p-5 mt-4" data-testid="report-form">
                <h3 className="font-head font-black text-lg mb-3">Report {m.name}</h3>
                <NbSelect value={report.reason} onChange={(e) => setReport({ ...report, reason: e.target.value })} className="mb-3">
                  <option>Inappropriate content</option><option>Fake profile</option><option>Harassment</option><option>Spam / scam</option><option>Other</option>
                </NbSelect>
                <NbTextarea rows={3} placeholder="Additional details (optional)" value={report.details} onChange={(e) => setReport({ ...report, details: e.target.value })} className="mb-3" data-testid="report-details" />
                <NbButton onClick={submitReport} variant="danger" className="w-full" data-testid="report-submit">Submit Report</NbButton>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
